import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useDropzone } from "react-dropzone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Scan, Sparkles, RefreshCw, Check, Upload, FileText, Eye, ChevronDown, ChevronUp, MapPin, Calendar } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Driver } from "@/hooks/useVehicles";
import { SmartDocumentViewer } from "@/components/viewer/SmartDocumentViewer";

const LICENSE_CATEGORIES = [
  { value: "AM", label: "AM (Cyclomoteur)" },
  { value: "A1", label: "A1 (Moto légère)" },
  { value: "A2", label: "A2 (Moto)" },
  { value: "A", label: "A (Toutes motos)" },
  { value: "B", label: "B (Voiture)" },
  { value: "BE", label: "BE (Remorque)" },
  { value: "C1", label: "C1 (Poids lourd léger)" },
  { value: "C", label: "C (Poids lourd)" },
  { value: "CE", label: "CE (Super poids lourd)" },
  { value: "D1", label: "D1 (Minibus)" },
  { value: "D", label: "D (Autocar)" },
];

const driverSchema = z.object({
  first_name: z.string().min(1, "Prénom requis"),
  last_name: z.string().min(1, "Nom requis"),
  email: z.string().email("Email invalide"),
  phone: z.string().nullable(),
  // Identity fields for CERFA
  gender: z.enum(["M", "F"]).nullable(),
  birth_date: z.string().nullable(),
  birth_place: z.string().nullable(),
  birth_department: z.string().nullable(),
  maiden_name: z.string().nullable(),
  // Address fields for CERFA
  address_number: z.string().nullable(),
  address_street: z.string().nullable(),
  address_complement: z.string().nullable(),
  postal_code: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  // License fields
  license_number: z.string().nullable(),
  license_expiry: z.string().nullable(),
  license_categories: z.array(z.string()),
  license_issued_date: z.string().nullable(),
  license_issued_by: z.string().nullable(),
});

type DriverFormValues = z.infer<typeof driverSchema>;

export type DriverInsert = Omit<Driver, "id" | "user_id" | "access_token" | "created_at" | "invitation_sent_at" | "portal_last_access_at">;

interface DriverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driver?: Driver | null;
  onSubmit: (data: DriverInsert) => Promise<void>;
}

type ScanState = "idle" | "scanning" | "success" | "error";

export function DriverDialog({ open, onOpenChange, driver, onSubmit }: DriverDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scanState, setScanState] = useState<ScanState>("idle");
  const [scannedFile, setScannedFile] = useState<File | null>(null);
  const [uploadedFilePath, setUploadedFilePath] = useState<string | null>(null);
  const [saveDocument, setSaveDocument] = useState(true);
  const [autoFilledFields, setAutoFilledFields] = useState<Set<string>>(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showCerfaFields, setShowCerfaFields] = useState(false);

  const form = useForm<DriverFormValues>({
    resolver: zodResolver(driverSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: null,
      gender: null,
      birth_date: null,
      birth_place: null,
      birth_department: null,
      maiden_name: null,
      address_number: null,
      address_street: null,
      address_complement: null,
      postal_code: null,
      city: null,
      country: "FRANCE",
      license_number: null,
      license_expiry: null,
      license_categories: [],
      license_issued_date: null,
      license_issued_by: null,
    },
  });

  useEffect(() => {
    if (driver) {
      form.reset({
        first_name: driver.first_name,
        last_name: driver.last_name,
        email: driver.email,
        phone: driver.phone,
        gender: (driver as any).gender || null,
        birth_date: (driver as any).birth_date || null,
        birth_place: (driver as any).birth_place || null,
        birth_department: (driver as any).birth_department || null,
        maiden_name: (driver as any).maiden_name || null,
        address_number: (driver as any).address_number || null,
        address_street: (driver as any).address_street || null,
        address_complement: (driver as any).address_complement || null,
        postal_code: (driver as any).postal_code || null,
        city: (driver as any).city || null,
        country: (driver as any).country || "FRANCE",
        license_number: driver.license_number,
        license_expiry: driver.license_expiry,
        license_categories: driver.license_categories || [],
        license_issued_date: (driver as any).license_issued_date || null,
        license_issued_by: (driver as any).license_issued_by || null,
      });
      // Show CERFA fields if driver has CERFA data
      if ((driver as any).birth_date || (driver as any).address_street) {
        setShowCerfaFields(true);
      }
    } else {
      form.reset({
        first_name: "",
        last_name: "",
        email: "",
        phone: null,
        gender: null,
        birth_date: null,
        birth_place: null,
        birth_department: null,
        maiden_name: null,
        address_number: null,
        address_street: null,
        address_complement: null,
        postal_code: null,
        city: null,
        country: "FRANCE",
        license_number: null,
        license_expiry: null,
        license_categories: [],
        license_issued_date: null,
        license_issued_by: null,
      });
    }
    // Reset scan state when dialog opens/closes
    setScanState("idle");
    setScannedFile(null);
    setUploadedFilePath(null);
    setAutoFilledFields(new Set());
    setPreviewUrl(null);
    setPreviewOpen(false);
  }, [driver, form, open]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setScanState("scanning");
    setScannedFile(file);
    setPreviewUrl(null); // Reset pending server URL

    try {
      // Get current user
      console.log("[DriverDialog] Starting scan...");
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error("[DriverDialog] Auth error:", authError);
        throw new Error(`Erreur auth: ${authError.message}`);
      }
      
      if (!user) {
        throw new Error("Non authentifié - veuillez vous reconnecter");
      }
      console.log("[DriverDialog] User authenticated:", user.id);

      // Upload directly to permanent location (covered by RLS policies)
      const filePath = `${user.id}/licenses/${Date.now()}-${file.name}`;
      console.log("[DriverDialog] Uploading to:", filePath);
      
      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(filePath, file);

      if (uploadError) {
        console.error("[DriverDialog] Upload error:", uploadError);
        throw new Error(`Erreur upload: ${uploadError.message}`);
      }
      console.log("[DriverDialog] Upload success");
      setUploadedFilePath(filePath); // Store for later document creation

      // Generate signed URL for server-first preview
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from("documents")
        .createSignedUrl(filePath, 3600); // 1 hour validity

      if (signedUrlError) {
        console.warn("[DriverDialog] Signed URL error:", signedUrlError);
      } else if (signedUrlData?.signedUrl) {
        setPreviewUrl(signedUrlData.signedUrl);
        console.log("[DriverDialog] Server preview URL generated");
      }

      // Call extract-document-info with driver_license type
      console.log("[DriverDialog] Calling extract-document-info...");
      const { data, error } = await supabase.functions.invoke("extract-document-info", {
        body: {
          fileName: file.name,
          mimeType: file.type,
          filePath,
          document_type: "driver_license"
        }
      });

      console.log("[DriverDialog] Edge function response:", { data, error });

      if (error) {
        console.error("[DriverDialog] Edge function error:", error);
        throw new Error(`Erreur extraction: ${error.message || JSON.stringify(error)}`);
      }

      // Check if data contains an error from the edge function
      if (data?.error) {
        console.warn("[DriverDialog] Extraction returned error:", data.error);
        throw new Error(`Extraction échouée: ${data.error}`);
      }

      // Map extracted data to form fields
      const filledFields = new Set<string>();

      if (data?.first_name) {
        form.setValue("first_name", data.first_name);
        filledFields.add("first_name");
      }
      if (data?.last_name) {
        form.setValue("last_name", data.last_name);
        filledFields.add("last_name");
      }
      if (data?.license_number) {
        form.setValue("license_number", data.license_number);
        filledFields.add("license_number");
      }
      if (data?.license_expiry) {
        form.setValue("license_expiry", data.license_expiry);
        filledFields.add("license_expiry");
      }
      if (data?.license_categories && Array.isArray(data.license_categories)) {
        // Normalize categories (uppercase, trim)
        const normalizedCategories = data.license_categories
          .map((c: string) => c.toUpperCase().trim())
          .filter((c: string) => LICENSE_CATEGORIES.some(lc => lc.value === c));
        if (normalizedCategories.length > 0) {
          form.setValue("license_categories", normalizedCategories);
          filledFields.add("license_categories");
        }
      }

      console.log("[DriverDialog] Filled fields:", [...filledFields]);
      setAutoFilledFields(filledFields);
      setScanState("success");
      toast.success("Données extraites par IA", {
        description: `${filledFields.size} champ(s) pré-rempli(s)`,
        icon: <Sparkles className="h-4 w-4" />
      });

    } catch (err: any) {
      console.error("[DriverDialog] Scan error:", err);
      setScanState("error");
      
      // Afficher l'erreur réelle dans le toast
      const errorMessage = err?.message || err?.error || String(err) || "Erreur inconnue";
      toast.error("Erreur lors de l'analyse", {
        description: errorMessage.length > 100 
          ? errorMessage.substring(0, 100) + "..." 
          : errorMessage
      });
    }
  }, [form]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".webp", ".heic"],
      "application/pdf": [".pdf"]
    },
    maxSize: 10 * 1024 * 1024, // 10 MB
    multiple: false,
    disabled: scanState === "scanning"
  });

  const resetScan = () => {
    setScanState("idle");
    setScannedFile(null);
    setAutoFilledFields(new Set());
  };

  const handleSubmit = async (values: DriverFormValues) => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      let licenseDocumentId: string | null = null;

      // If we have a scanned file and user wants to save it, create a document first
      if (saveDocument && scannedFile && uploadedFilePath) {
        console.log("[DriverDialog] Creating document record for license...");
        
        // File is already in permanent location, no need to move
        // Create document record with the permanent path
        const { data: docData, error: docError } = await supabase
          .from("documents")
          .insert([{
            user_id: user.id,
            title: `Permis de conduire - ${values.first_name} ${values.last_name}`,
            file_name: scannedFile.name,
            file_path: uploadedFilePath,
            mime_type: scannedFile.type,
            file_size: scannedFile.size,
            doc_type: "other" as const,
            source: "upload" as const,
            status: "complete" as const,
            category: "vehicle" as const,
          }])
          .select("id")
          .single();

        if (docError) {
          console.error("[DriverDialog] Document creation error:", docError);
          toast.error("Erreur lors de la sauvegarde du document");
        } else if (docData) {
          licenseDocumentId = docData.id;
          console.log("[DriverDialog] Document created with ID:", licenseDocumentId);
        }
      }

      // Create driver with optional license document link
      await onSubmit({
        ...values,
        status: driver?.status || "active",
        license_document_id: licenseDocumentId,
      } as DriverInsert);

      if (licenseDocumentId) {
        toast.success("Conducteur et permis enregistrés", {
          description: "Le scan du permis a été archivé et lié au conducteur"
        });
      }

      onOpenChange(false);
    } catch (err: any) {
      console.error("[DriverDialog] Submit error:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAutoFillBadge = (fieldName: string) => {
    if (!autoFilledFields.has(fieldName)) return null;
    return (
      <span className="ml-2 inline-flex items-center gap-1 text-xs text-primary">
        <Sparkles className="h-3 w-3" />
        IA
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {driver ? "Modifier le conducteur" : "Ajouter un conducteur"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            
            {/* Scan Zone */}
            {!driver && (
              <>
                <div
                  {...getRootProps()}
                  className={`
                    border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer
                    ${isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"}
                    ${scanState === "scanning" ? "pointer-events-none opacity-75" : ""}
                    ${scanState === "success" ? "border-green-500 bg-green-50 dark:bg-green-950/20" : ""}
                    ${scanState === "error" ? "border-destructive bg-destructive/5" : ""}
                  `}
                >
                  <input {...getInputProps()} />
                  
                  {scanState === "idle" && (
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-3 rounded-full bg-muted">
                        <Scan className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="font-medium text-sm">Scanner le permis de conduire</p>
                      <p className="text-xs text-muted-foreground">
                        Glissez-déposez ou cliquez pour remplir automatiquement
                      </p>
                      <p className="text-xs text-muted-foreground/70">
                        JPG, PNG, PDF • Max 10 Mo
                      </p>
                    </div>
                  )}

                  {scanState === "scanning" && (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="font-medium text-sm">Analyse du permis en cours...</p>
                      <p className="text-xs text-muted-foreground">Cela peut prendre quelques secondes</p>
                    </div>
                  )}

                  {scanState === "success" && (
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                        <Check className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                      <p className="font-medium text-sm flex items-center gap-1.5 text-green-700 dark:text-green-400">
                        <Sparkles className="h-4 w-4" />
                        Données extraites par IA
                      </p>
                      {scannedFile && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <FileText className="h-3 w-3" />
                          <span className="max-w-[150px] truncate">{scannedFile.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 p-0 hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewOpen(true);
                            }}
                            title="Voir le document scanné"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); resetScan(); }}
                        className="mt-1"
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />
                        Scanner un autre
                      </Button>
                    </div>
                  )}

                  {scanState === "error" && (
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-2 rounded-full bg-destructive/10">
                        <Upload className="h-6 w-6 text-destructive" />
                      </div>
                      <p className="font-medium text-sm text-destructive">Échec de l'analyse</p>
                      <p className="text-xs text-muted-foreground">Cliquez pour réessayer</p>
                    </div>
                  )}
                </div>

                {/* Save document checkbox */}
                {scannedFile && scanState === "success" && (
                  <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
                    <Checkbox 
                      id="save-license" 
                      checked={saveDocument}
                      onCheckedChange={(checked) => setSaveDocument(checked === true)}
                    />
                    <label 
                      htmlFor="save-license" 
                      className="text-sm cursor-pointer flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      Sauvegarder ce scan comme document "Permis de conduire"
                    </label>
                  </div>
                )}

                {/* Separator */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      ou remplir manuellement
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* First name + Last name */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">
                      Prénom *
                      {renderAutoFillBadge("first_name")}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Jean" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">
                      Nom *
                      {renderAutoFillBadge("last_name")}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Dupont" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jean.dupont@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Phone */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Téléphone</FormLabel>
                  <FormControl>
                    <Input 
                      type="tel" 
                      placeholder="06 12 34 56 78" 
                      {...field} 
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* CERFA Fields Toggle */}
            <div className="border rounded-lg">
              <button
                type="button"
                onClick={() => setShowCerfaFields(!showCerfaFields)}
                className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-muted/50 rounded-lg transition-colors"
              >
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  Informations CERFA (désignation)
                </span>
                {showCerfaFields ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {showCerfaFields && (
                <div className="p-3 pt-0 space-y-4">
                  <p className="text-xs text-muted-foreground mb-4">
                    Ces informations sont nécessaires pour générer automatiquement le formulaire CERFA de désignation en cas d'amende.
                  </p>

                  {/* Gender */}
                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sexe</FormLabel>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              checked={field.value === "M"}
                              onChange={() => field.onChange("M")}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Masculin</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              checked={field.value === "F"}
                              onChange={() => field.onChange("F")}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">Féminin</span>
                          </label>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Birth date and place */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="birth_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Date de naissance
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="date"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="birth_place"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Lieu de naissance</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Paris"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Birth department and maiden name */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="birth_department"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Département de naissance</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="75"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="maiden_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nom d'épouse</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Optionnel"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Address section */}
                  <div className="pt-2">
                    <p className="text-sm font-medium flex items-center gap-1 mb-3">
                      <MapPin className="h-4 w-4" />
                      Adresse
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      <FormField
                        control={form.control}
                        name="address_number"
                        render={({ field }) => (
                          <FormItem className="col-span-1">
                            <FormLabel className="text-xs">N°</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="12"
                                {...field}
                                value={field.value || ""}
                                onChange={(e) => field.onChange(e.target.value || null)}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="address_street"
                        render={({ field }) => (
                          <FormItem className="col-span-3">
                            <FormLabel className="text-xs">Voie</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="rue des Lilas"
                                {...field}
                                value={field.value || ""}
                                onChange={(e) => field.onChange(e.target.value || null)}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="address_complement"
                      render={({ field }) => (
                        <FormItem className="mt-2">
                          <FormLabel className="text-xs">Complément</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Bâtiment A, Apt 12 (optionnel)"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => field.onChange(e.target.value || null)}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <FormField
                        control={form.control}
                        name="postal_code"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Code postal</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="75001"
                                {...field}
                                value={field.value || ""}
                                onChange={(e) => field.onChange(e.target.value || null)}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem className="col-span-2">
                            <FormLabel className="text-xs">Ville</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Paris"
                                {...field}
                                value={field.value || ""}
                                onChange={(e) => field.onChange(e.target.value || null)}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem className="mt-2">
                          <FormLabel className="text-xs">Pays</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="FRANCE"
                              {...field}
                              value={field.value || "FRANCE"}
                              onChange={(e) => field.onChange(e.target.value || "FRANCE")}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* License issued by */}
                  <div className="pt-2">
                    <p className="text-sm font-medium mb-3">Délivrance du permis</p>
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="license_issued_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Délivré le</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                {...field}
                                value={field.value || ""}
                                onChange={(e) => field.onChange(e.target.value || null)}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="license_issued_by"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Par</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Préfecture de Paris"
                                {...field}
                                value={field.value || ""}
                                onChange={(e) => field.onChange(e.target.value || null)}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* License number */}
            <FormField
              control={form.control}
              name="license_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    Numéro de permis
                    {renderAutoFillBadge("license_number")}
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="12AB34567" 
                      {...field} 
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      className="font-mono uppercase"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* License expiry */}
            <FormField
              control={form.control}
              name="license_expiry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    Date d'expiration du permis
                    {renderAutoFillBadge("license_expiry")}
                  </FormLabel>
                  <FormControl>
                    <Input 
                      type="date" 
                      {...field} 
                      value={field.value || ""} 
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* License categories */}
            <FormField
              control={form.control}
              name="license_categories"
              render={() => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    Catégories de permis
                    {renderAutoFillBadge("license_categories")}
                  </FormLabel>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {LICENSE_CATEGORIES.map((category) => (
                      <FormField
                        key={category.value}
                        control={form.control}
                        name="license_categories"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(category.value)}
                                onCheckedChange={(checked) => {
                                  const current = field.value || [];
                                  if (checked) {
                                    field.onChange([...current, category.value]);
                                  } else {
                                    field.onChange(current.filter((v) => v !== category.value));
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal cursor-pointer">
                              {category.value}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {driver ? "Enregistrer" : "Ajouter"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl w-[90vw] h-[80vh] p-0 flex flex-col">
          <DialogHeader className="p-4 pb-2 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Aperçu du permis scanné
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 p-4">
            {previewUrl && scannedFile && (
              <SmartDocumentViewer
                url={previewUrl}
                title={scannedFile.name}
                mimeType={scannedFile.type}
                showControls={true}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}